import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneSheet } from '../../chats/phone/PhoneSheet'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { useMediaSrc } from '../../library/useMediaSrc'
import { Dialog } from '../../of/home/Dialog'
import { fanslyChatUrl, initialsOf } from './format'
import type { ChatRow } from './model'

/** Fansly's report reasons (its report dialog): ToS, DMCA, spam, abuse, scam. */
export const REPORT_REASONS = [1, 2, 3, 4, 5] as const

export interface ChatActions {
  reload: () => void
  /** Fansly account ignore flag 1 (Fansly's own "Mute" in a conversation). */
  toggleMute: () => Promise<void>
  toggleReceipts: () => Promise<void>
  hide: () => Promise<void>
  /** Ignore flag 2. */
  toggleBlock: () => Promise<void>
  report: (reason: number, comment: string) => Promise<void>
}

export interface ChatFlags {
  muted: boolean
  blocked: boolean
  receipts: boolean
}

interface Props {
  row: ChatRow
  flags: ChatFlags
  canWrite: boolean
  actions: ChatActions
  phone: boolean
  /** Phones: back to the list. */
  onBack?: () => void
  /** The fan column: shown beside (desktop) or as a sheet (phone). */
  side: { open: boolean; toggle: () => void }
  refreshing: boolean
  /** Extra buttons the host puts first in the header's actions (Workspace's pin, ADR 0045). */
  tools?: ReactNode
}

type Confirm = 'hide' | 'block' | null

function ReportDialog({ name, onSend, onClose }: { name: string; onSend: (reason: number, comment: string) => Promise<void>; onClose: () => void }) {
  const { t } = useTranslation('fanslyChats')
  const [reason, setReason] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const send = async () => {
    if (!reason || !comment.trim()) return setError(t('report.missing'))
    setBusy(true)
    setError('')
    try {
      await onSend(reason, comment.trim())
      onClose()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      title={t('report.title', { name })}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="lib-btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={busy || !reason || !comment.trim()} onClick={() => void send()}>
            {t('report.send')}
          </button>
        </>
      }
    >
      <div className="fsc-report" role="radiogroup" aria-label={t('report.reason')}>
        {REPORT_REASONS.map((r) => (
          <label key={r} className={`fsc-report-reason ${reason === r ? 'is-on' : ''}`}>
            <input type="radio" name="fsc-report" checked={reason === r} onChange={() => setReason(r)} />
            {t(`report.reasons.${r}`)}
          </label>
        ))}
        <textarea className="fsc-textarea" rows={3} value={comment} placeholder={t('report.comment')} aria-label={t('report.comment')} onChange={(e) => setComment(e.target.value)} />
        {error && <span className="rc-error">{error}</span>}
      </div>
    </Dialog>
  )
}

/** The open chat's top: the fan, reload, mute, read receipts, the fan column and More (hide, block, report, open on Fansly). */
export function ChatHeader({ row, flags, canWrite, actions, phone, onBack, side, refreshing, tools }: Props) {
  const { t } = useTranslation('fanslyChats')
  const [menu, setMenu] = useState(false)
  const [confirm, setConfirm] = useState<Confirm>(null)
  const [reporting, setReporting] = useState(false)
  const [notice, setNotice] = useState('')
  const pic = useMediaSrc(row.avatar)
  const box = useRef<HTMLDivElement>(null)
  const name = row.name || (row.username ? `@${row.username}` : t('list.fan'))

  useEffect(() => {
    if (!menu || phone) return
    const away = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setMenu(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false)
    window.addEventListener('mousedown', away)
    window.addEventListener('keydown', esc)
    return () => {
      window.removeEventListener('mousedown', away)
      window.removeEventListener('keydown', esc)
    }
  }, [menu, phone])

  const act = (run: () => Promise<void>) => () => {
    setMenu(false)
    setNotice('')
    run().catch((e: unknown) => setNotice((e as Error).message))
  }
  const openFansly = () => {
    setMenu(false)
    window.open(fanslyChatUrl(row.groupId), '_blank', 'noopener,noreferrer')
  }

  type Item = { key: string; icon: string; label: string; danger?: boolean; run: () => void }
  const items: Item[] = [
    { key: 'reload', icon: 'rotate-cw', label: t('header.reload'), run: () => (setMenu(false), actions.reload()) },
    ...(canWrite
      ? [
          { key: 'mute', icon: flags.muted ? 'bell' : 'bell-off', label: flags.muted ? t('header.unmute') : t('header.mute'), run: act(actions.toggleMute) },
          { key: 'receipts', icon: flags.receipts ? 'eye-off' : 'eye', label: flags.receipts ? t('header.receiptsOff') : t('header.receiptsOn'), run: act(actions.toggleReceipts) },
        ]
      : []),
    { key: 'fansly', icon: 'external-link', label: t('header.openFansly'), run: openFansly },
    ...(canWrite
      ? [
          { key: 'hide', icon: 'archive', label: t('header.hide'), danger: true, run: () => (setMenu(false), setConfirm('hide')) },
          { key: 'block', icon: 'slash', label: flags.blocked ? t('header.unblock') : t('header.block'), danger: !flags.blocked, run: () => (setMenu(false), flags.blocked ? act(actions.toggleBlock)() : setConfirm('block')) },
          { key: 'report', icon: 'flag', label: t('header.report'), danger: true, run: () => (setMenu(false), setReporting(true)) },
        ]
      : []),
  ]

  const dialogs = (
    <>
      {confirm && (
        <ConfirmPopover
          title={t(`header.${confirm}Title`, { name })}
          body={t(`header.${confirm}Body`, { name })}
          confirmLabel={t(`header.${confirm}`)}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const which = confirm
            setConfirm(null)
            act(which === 'hide' ? actions.hide : actions.toggleBlock)()
          }}
        />
      )}
      {reporting && <ReportDialog name={name} onSend={actions.report} onClose={() => setReporting(false)} />}
    </>
  )

  if (phone) {
    return (
      <div className="ph-chat-head" role="group" aria-label={t('header.label', { name })}>
        <div className="ph-chat-bar">
          <button type="button" className="ph-back" aria-label={t('header.back')} onClick={onBack}>
            <FeatherIcon name="chevron-left" size={26} />
          </button>
          <button type="button" className="ph-chat-who" aria-label={t('header.fanInfoFor', { name })} onClick={side.toggle}>
            <span className="ph-chat-avatar">
              <span className="ph-chat-pic" ref={pic.ref as React.RefObject<HTMLSpanElement>}>
                {pic.src ? <img src={pic.src} alt="" /> : initialsOf(name)}
              </span>
            </span>
            <span className="ph-chat-names">
              <span className="ph-chat-name">
                <span>{name}</span>
                <FeatherIcon name="chevron-down" size={14} />
              </span>
              {row.username && <span className="ph-chat-seen">@{row.username}</span>}
            </span>
          </button>
          {flags.muted && (
            <span className="ph-icon-btn fsc-muted-mark" role="img" aria-label={t('header.muted')}>
              <FeatherIcon name="bell-off" size={17} />
            </span>
          )}
          {tools}
          <button type="button" className="ph-icon-btn ph-chat-more" aria-label={t('header.more')} aria-haspopup="dialog" onClick={() => setMenu(true)}>
            <FeatherIcon name="more-horizontal" size={20} />
          </button>
        </div>
        {notice && (
          <div className="ph-chat-notice" role="status">
            <span>{notice}</span>
            <button type="button" aria-label={t('close')} onClick={() => setNotice('')}>
              <FeatherIcon name="x" size={14} />
            </button>
          </div>
        )}
        <PhoneSheet open={menu} onClose={() => setMenu(false)} label={t('header.more')}>
          <div className="ph-menu">
            <button type="button" className="ph-menu-item" onClick={() => (setMenu(false), side.toggle())}>
              <FeatherIcon name="user" size={19} />
              {t('header.fanInfo')}
            </button>
            {items.map((i) => (
              <button key={i.key} type="button" className={`ph-menu-item ${i.danger ? 'is-danger' : ''}`} onClick={i.run}>
                <FeatherIcon name={i.icon} size={19} />
                {i.label}
              </button>
            ))}
          </div>
        </PhoneSheet>
        {dialogs}
      </div>
    )
  }

  return (
    <div className="chat-window-header fsc-head" role="group" aria-label={t('header.label', { name })}>
      <span className="fsc-head-avatar" ref={pic.ref as React.RefObject<HTMLSpanElement>} aria-hidden="true">
        {pic.src ? <img src={pic.src} alt="" /> : initialsOf(name)}
      </span>
      <div className="chat-window-user">
        <div className="chat-window-name-row">
          <span className="chat-window-name">{name}</span>
          {row.username && <span className="chat-window-handle">@{row.username}</span>}
          {flags.muted && (
            <span className="fsc-head-flag" role="img" aria-label={t('header.muted')} title={t('header.muted')}>
              <FeatherIcon name="bell-off" size={14} />
            </span>
          )}
          {flags.blocked && <span className="fsc-head-pill is-bad">{t('header.blocked')}</span>}
        </div>
        <div className="chat-window-actions chat-window-actions--sub">
          {row.subscriber && <span className="fsc-head-pill">{t('fan.subscriber')}</span>}
          {row.followsYou && <span className="fsc-head-pill">{t('fan.followsYou')}</span>}
          {canWrite && (
            <button type="button" className={`chat-head-tool-btn ${flags.muted ? 'active' : ''}`} aria-label={flags.muted ? t('header.unmute') : t('header.mute')} title={flags.muted ? t('header.unmute') : t('header.mute')} aria-pressed={flags.muted} onClick={act(actions.toggleMute)}>
              <FeatherIcon name={flags.muted ? 'bell-off' : 'bell'} size={15} />
            </button>
          )}
          {canWrite && (
            <button type="button" className={`chat-head-tool-btn ${flags.receipts ? '' : 'active'}`} aria-label={flags.receipts ? t('header.receiptsOff') : t('header.receiptsOn')} title={flags.receipts ? t('header.receiptsOnState') : t('header.receiptsOffState')} aria-pressed={!flags.receipts} onClick={act(actions.toggleReceipts)}>
              <FeatherIcon name={flags.receipts ? 'eye' : 'eye-off'} size={15} />
            </button>
          )}
          <a className="chat-head-tab-btn" href={fanslyChatUrl(row.groupId)} target="_blank" rel="noopener noreferrer" title={t('header.openFansly')}>
            <FeatherIcon name="external-link" size={14} />
            <span className="chat-head-tab-label">{t('header.fansly')}</span>
          </a>
        </div>
        {notice && (
          <div className="chat-head-notice" role="status">
            {notice}
            <button type="button" aria-label={t('close')} onClick={() => setNotice('')}>
              <FeatherIcon name="x" size={12} />
            </button>
          </div>
        )}
      </div>
      <div className="chat-window-actions chat-window-actions--top" ref={box}>
        {tools}
        <button type="button" className={`chat-head-icon-btn ${refreshing ? 'om-busy' : ''}`} aria-label={t('header.reload')} title={t('header.reload')} onClick={actions.reload}>
          <FeatherIcon name="rotate-cw" size={18} />
        </button>
        {!side.open && (
          <button type="button" className="chat-head-icon-btn" aria-label={t('header.fanInfo')} title={t('header.fanInfo')} onClick={side.toggle}>
            <FeatherIcon name="panel-right-open" size={20} />
          </button>
        )}
        <button type="button" className="chat-head-icon-btn chat-more-btn" aria-label={t('header.more')} title={t('header.more')} aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
          <FeatherIcon name="more-vertical" size={20} />
        </button>
        {menu && (
          <div className="chat-more-menu" role="menu">
            {items.map((i) => (
              <button key={i.key} type="button" role="menuitem" className={i.danger ? 'danger' : ''} onClick={i.run}>
                {i.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {dialogs}
    </div>
  )
}
